import { formConfig, type FormConfig } from '@cod/config-schema';

/**
 * Which control in the Designer is blocking the save, and why.
 *
 * 🚨 Derived from `formConfig` — the SAME schema `validateFormConfig` rejects the request with
 * (`admin.controller.ts:47` → 422). A hand-written copy of the rules would be a second source of
 * truth that drifts the moment a bound changes in `config-schema`, and the failure mode of that
 * drift is precisely the one this module exists to remove: a save that is refused with nothing on
 * screen saying which field did it.
 *
 * The merchant could always reach this state — every `.min(1)` string in the config is an input
 * they can clear — and until now the only feedback was a "Could not save changes" toast, with the
 * offending control often scrolled off the panel or sitting on a different tab.
 */

/**
 * One issue from `formConfig.safeParse`, derived from the schema rather than imported from `zod`.
 *
 * The admin does not depend on `zod` directly and should not start: `config-schema` owns that
 * dependency, and pulling a second copy into this app risks two zod versions disagreeing about
 * what an issue looks like.
 */
type ConfigIssue = Extract<
  ReturnType<typeof formConfig.safeParse>,
  { success: false }
>['error']['issues'][number];

/** One control's problem: an i18n key plus the numbers its copy interpolates. */
export interface FieldProblem {
  /** i18n key under `designer.validation`. */
  key: string;
  /** Interpolation values (`min` / `max`), when the message quotes a bound. */
  params?: Record<string, number>;
}

export type ProblemMap = ReadonlyMap<string, FieldProblem>;

/**
 * Translate one zod issue into merchant copy.
 *
 * Only the shapes this config can actually produce are named; anything else falls through to a
 * generic "not valid", which is still infinitely better than the silent toast. `too_small` on a
 * string with `minimum === 1` is the overwhelmingly common case — an emptied text box — and gets
 * its own wording rather than "at least 1 character", which reads like a puzzle.
 */
function describe(issue: ConfigIssue): FieldProblem {
  if (issue.code === 'too_small') {
    if (issue.type === 'string') {
      const min = Number(issue.minimum);
      return min <= 1 ? { key: 'empty' } : { key: 'minChars', params: { min } };
    }
    if (issue.type === 'number') {
      return { key: 'minValue', params: { min: Number(issue.minimum) } };
    }
    if (issue.type === 'array') {
      return { key: 'minItems', params: { min: Number(issue.minimum) } };
    }
  }
  if (issue.code === 'too_big') {
    if (issue.type === 'string') return { key: 'maxChars', params: { max: Number(issue.maximum) } };
    if (issue.type === 'number') return { key: 'maxValue', params: { max: Number(issue.maximum) } };
  }
  if (issue.code === 'invalid_type' && issue.received === 'undefined') return { key: 'empty' };
  return { key: 'invalid' };
}

/**
 * A stable alias for a per-field control's path.
 *
 * `fields.3.label` is useless to `CustomFieldControls`, which is handed a field and no index —
 * and an index would go stale the moment the merchant drags the field somewhere else. The field's
 * `key` is its identity everywhere else in this app, so problems on a field are ALSO published
 * under `field:<key>.<prop>` and looked up that way.
 */
export function fieldProblemPath(fieldKey: string, prop: string): string {
  return `field:${fieldKey}.${prop}`;
}

/**
 * Every problem in the draft, keyed by the path of the control that owns it.
 *
 * Returns an empty map for a valid draft, so `size === 0` is "saveable". The first issue per path
 * wins: a control shows one message, and zod reports them in schema order, which is the order the
 * merchant reads.
 */
export function configProblems(draft: FormConfig): ProblemMap {
  const parsed = formConfig.safeParse(draft);
  const problems = new Map<string, FieldProblem>();
  if (parsed.success) return problems;

  const fields = Array.isArray(draft.fields) ? draft.fields : [];
  for (const issue of parsed.error.issues) {
    const path = issue.path.join('.');
    if (!path || problems.has(path)) continue;
    const problem = describe(issue);
    problems.set(path, problem);

    // Publish the field-key alias alongside the raw path — see `fieldProblemPath`.
    if (issue.path[0] === 'fields' && typeof issue.path[1] === 'number') {
      const key = fields[issue.path[1]]?.key;
      const prop = issue.path.slice(2).join('.');
      if (key && prop) problems.set(fieldProblemPath(key, prop), problem);
    }
  }
  return problems;
}

/** The Designer's tab ids, as composed in `FormDesigner`. */
export type DesignerTab = 'button' | 'fields' | 'coverage' | 'appearance' | 'text';

/**
 * Which tab edits a given config root — or `null` when the Designer does not edit it at all.
 *
 * 🚨 The `null` cases are the point. `shipping_rates`, `thank_you`, `tracking` and the quantity
 * offers under `_future` live on their OWN pages, so a stored problem in one of them still blocks
 * this save (the backend would refuse it too) but has no control here to mark. Routing those to a
 * tab anyway would send the merchant somewhere with nothing highlighted, which is worse than the
 * bare toast this whole change is replacing.
 *
 * Mirrors the tab composition in `FormDesigner`; `validation.test.ts` pins one path per tab.
 */
export function tabForPath(path: string): DesignerTab | null {
  switch (path.split('.')[0]) {
    case 'messages':
      return 'text';
    case 'button':
      return 'button';
    case 'coverage':
      return 'coverage';
    case 'appearance':
    case 'fields_style':
      return 'appearance';
    case 'header':
    case 'fields':
    case 'summary':
    case 'discount':
    case 'product_card':
    case 'totals_style':
    case 'shipping_method':
    case 'consent':
    case 'submit':
      return 'fields';
    default:
      return null;
  }
}

/**
 * The first problem the merchant can actually act on, with the tab that shows it.
 *
 * Save uses this to jump them to the right tab. `null` means the draft is either clean or broken
 * only where this view cannot reach — the caller falls back to generic copy.
 *
 * 🚨 `visibleTabs` is not optional decoration. The Button tab exists ONLY in popup mode, and
 * `button.text` is a `.min(1)` string — so a merchant who empties it, switches to embedded and
 * saves would otherwise be sent to a tab that is not rendered, land on Fields by the fallback in
 * `FormDesigner`, and be told to check red fields that are all fine.
 */
export function firstActionable(
  problems: ProblemMap,
  visibleTabs: readonly DesignerTab[],
): { path: string; problem: FieldProblem; tab: DesignerTab } | null {
  for (const [path, problem] of problems) {
    // Skip the `field:<key>` aliases — they duplicate a raw path already in the map.
    if (path.startsWith('field:')) continue;
    const tab = tabForPath(path);
    if (tab && visibleTabs.includes(tab)) return { path, problem, tab };
  }
  return null;
}
